import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LockupLogo, BRAND_ASSETS, useTheme, ThemeToggle } from "@/design-system";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import { useIsDesktop } from "@/lib/useMediaQuery";
import { Home, Compass, PlusCircle, Users, User, Heart, Search } from "lucide-react";
import { Outlet } from "react-router-dom";
import { trackPageview } from "@/lib/analytics";
import NotificationsPanel from "@/pages/member/NotificationsPanel";
import InsiderUnlocksSheet from "@/components/InsiderUnlocksSheet";
import GuestIntro from "@/components/GuestIntro";
import Confetti from "@/components/Confetti";
import { consumeSignupFlag, SIGNUP_CELEBRATION_EVENT } from "@/lib/unlocks";
import { WELCOME_AFTER_SIGNUP } from "@/lib/levels";

/**
 * MemberShell · Wave 15 Step 9 (Founder 2 Oct 2026).
 * PHONE header, L→R: "hello, name" | logo (centred) | theme toggle | heart.
 * DESKTOP header, L→R: logo | nav | theme | heart | hello name | avatar.
 * Bookmark removed from both. Heart carries the 9px sherehe-pink unread dot.
 * Yap/squad unread shows as a pink dot on the squad tab (phone + desktop).
 */
const TABS = [
  { to: "/app",          label: "For You",  icon: Home,       testId: "tab-foryou",  end: true },
  { to: "/app/discover", label: "Discover", icon: Compass,    testId: "tab-discover" },
  { to: "/app/new",      label: "New",      icon: PlusCircle, testId: "tab-new", raised: true },
  { to: "/app/squad",    label: "Squad",    icon: Users,      testId: "tab-squad", badge: "yap" },
  { to: "/app/me",       label: "Profile",  icon: User,       testId: "tab-me" },
];

// WW-206.b · poll every 60 s, only while the tab is visible; one fetch when it comes back.
function useVisiblePoll(fn, active, ms = 60000) {
  useEffect(() => {
    if (!active) return undefined;
    fn();
    let t = setInterval(fn, ms);
    const onVis = () => {
      clearInterval(t);
      if (document.visibilityState === "visible") { fn(); t = setInterval(fn, ms); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}

function useYapUnread(user) {
  const [count, setCount] = useState(0);
  useEffect(() => { if (!user) setCount(0); }, [user]);
  useVisiblePoll(() => client.get("/yap/threads").then((r) => {
    const total = (r.data?.threads || []).reduce((s, t) => s + (t.unread_count || 0), 0);
    setCount(total);
  }).catch(() => {}), !!user);
  return count;
}

function useNotifications(user) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const load = () => {
    if (!user) { setUnread(0); setItems([]); return; }
    client.get("/notifications", { params: { limit: 20 } }).then((r) => {
      setUnread(r.data?.unread_count || 0);
      setItems(r.data?.notifications || []);
    }).catch(() => { setItems([]); });
  };
  useEffect(() => { if (!user) { setUnread(0); setItems([]); } }, [user]);
  useVisiblePoll(load, !!user);
  // Wave C bug O (Vania 7 Oct) · do NOT clear the dot locally without a
  // server call. The old `clearDotNow` set unread=0 in React state, and the
  // next 20s poll overwrote it with the real server count, which was still
  // unread · that is why notifications "came back as unread". If you want
  // to clear, call /mark-all-read first and only clear local on success.
  const markAllReadNow = async () => {
    try {
      await client.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((items || []).map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch {}
  };
  const markOneReadNow = async (id) => {
    try {
      await client.post(`/notifications/${id}/read`);
      setUnread((u) => Math.max(0, u - 1));
      setItems((items || []).map((n) => n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
    } catch {}
  };
  return { unread, items, refresh: load, markAllReadNow, markOneReadNow };
}

export default function MemberShell() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [topQ, setTopQ] = useState("");
  const { search: routeSearch } = useLocation();
  useEffect(() => {
    // WW-146.d · the top bar search always shows Discover's own query
    setTopQ(pathname === "/app/discover" ? (new URLSearchParams(routeSearch).get("q") || "") : "");
  }, [pathname, routeSearch]);
  const { user } = useAuth();
  const { theme, forcedNight } = useTheme();
  const logoGround = theme === "cream" ? "cream" : "night";
  const isDesktop = useIsDesktop();
  const onEvent = /^\/app\/event\//.test(pathname);
  const onProfile = /^\/app\/(me|people\/)/.test(pathname);
  const hideTop = !isDesktop && (onEvent || onProfile);
  const rosette = BRAND_ASSETS["pattern.rosette.night"].path;
  const yapUnread = useYapUnread(user);
  const notif = useNotifications(user);
  const [panelOpen, setPanelOpen] = useState(false);
  const firstName = user
    ? ((user.display_name || user.name || user.handle || "there") + "").trim().split(/\s+/)[0].toLowerCase()
    : "";

  useEffect(() => { trackPageview(pathname); }, [pathname]);
  // Wave C bug O (Vania 7 Oct) · opening the panel no longer clears the dot
  // locally. The dot clears only when a notification is actually marked
  // read server-side (click a row, or hit "mark all read"), so polling can
  // never bring back a count that was persisted as read.
  const openPanel = () => { setPanelOpen(true); };

  return (
    <div className="flex flex-col overflow-hidden"
         style={{ height: "100dvh", background: "var(--ww-ground)", color: "var(--ww-text)",
                  fontFamily: "var(--f-body)" }}>
      <DocumentTitle title="watu wako" />
      <div aria-hidden className="iko-wash"><span/><span/></div>
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0"
           style={{ backgroundImage: `url(${rosette})`, backgroundRepeat: "repeat",
                    backgroundSize: "480px 480px", opacity: 0.08 }} />

      {!hideTop && (
        isDesktop ? (
          <header data-testid="member-topbar" data-layout="desktop"
                  className="relative z-20 flex flex-shrink-0 items-center glass glass-chrome"
                  style={{ height: 64, color: "var(--ww-text)",
                           borderLeft: 0, borderRight: 0, borderTop: 0,
                           borderBottom: "1px solid var(--glass-border)" }}>
            <div className="ww-container flex items-center gap-6" style={{ height: 64, position: "relative" }}>
              <button aria-label="home" onClick={() => nav("/app")}
                      style={{ background: "transparent", border: 0, cursor: "pointer",
                               padding: 0, display: "flex", alignItems: "center" }}>
                <LockupLogo width={124} ground={logoGround} />
              </button>
              <nav className="flex items-center gap-1" data-testid="desktop-nav">
                {TABS.filter((t) => !t.raised).map((t) => {
                  return (
                    <NavLink key={t.to} to={t.to} end={t.end} data-testid={`desktop-${t.testId}`}
                             className={({ isActive }) =>
                               `px-3 py-1 text-[13px] lowercase rounded-full transition-colors inline-flex items-center gap-1 ${isActive ? "font-semibold" : ""}`}
                             style={({ isActive }) => ({
                               fontFamily: "var(--f-head)",
                               fontWeight: isActive ? 700 : 500,
                               color: isActive ? "var(--ww-text)" : "var(--ww-text-muted)",
                               background: isActive ? "rgba(255,214,10,0.10)" : "transparent",
                               border: isActive ? "1px solid rgba(255,214,10,0.35)" : "1px solid transparent",
                               position: "relative",
                             })}>
                      {t.label.toLowerCase()}
                      {t.badge === "yap" && yapUnread > 0 && (
                        <span data-testid="desktop-nav-yap-dot"
                              style={{ width: 9, height: 9, borderRadius: 999,
                                       background: "#FF6B9D", marginLeft: 2 }} />
                      )}
                    </NavLink>
                  );
                })}
                <button data-testid="desktop-tab-new" onClick={() => nav("/app/new")}
                        className="ml-2 flex items-center gap-1 rounded-full px-3 py-1 text-[13px] lowercase"
                        style={{ background: "var(--kili)", color: "#1A1A30",
                                 fontFamily: "var(--f-head)", fontWeight: 700,
                                 border: 0, cursor: "pointer" }}>
                  <PlusCircle size={14} strokeWidth={2.2} /> new
                </button>
              </nav>
              {/* WW-146.d · one search on every desktop page, from "new" to the theme switch */}
              <form data-testid="topbar-search-form" className="mx-3" style={{ flex: 1, minWidth: 0, position: "relative" }}
                    onSubmit={(e) => { e.preventDefault(); nav(`/app/discover?q=${encodeURIComponent(topQ)}`); }}>
                <Search size={14} aria-hidden="true" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ww-text-muted)" }} />
                <input data-testid="topbar-search-input" value={topQ} aria-label="search"
                       onChange={(e) => { setTopQ(e.target.value); if (pathname === "/app/discover") nav(`/app/discover?q=${encodeURIComponent(e.target.value)}`, { replace: true }); }}
                       placeholder="search events, venues, curators, watu"
                       style={{ width: "100%", height: 36, padding: "0 12px 0 32px", borderRadius: 999,
                                background: "var(--ww-sunken)", border: "1px solid var(--ww-line)",
                                color: "var(--ww-text)", fontFamily: "Arial", fontSize: 13, outline: "none" }} />
              </form>
              <div className="ml-auto flex items-center gap-2">
                {!forcedNight && <ThemeToggle size={18} />}
                {user && (
                  <HeartButton unread={notif.unread} onClick={openPanel}
                                 label={`notifications${notif.unread ? `, ${notif.unread} new` : ""}`}
                                 testId="topbar-notifications" />
                )}
                {user ? (
                  <button data-testid="topbar-greeting"
                          onClick={() => nav("/app/me")}
                          className="flex items-center gap-2"
                          style={{ background: "transparent", border: 0, cursor: "pointer",
                                   padding: "4px 6px", borderRadius: 999, color: "var(--ww-text)" }}>
                    <span className="lowercase"
                          style={{ fontFamily: "var(--f-head)", fontWeight: 500,
                                   fontSize: 13, color: "var(--ww-text-muted)" }}>
                      hello,&nbsp;
                      <span style={{ color: "var(--ww-text)", fontWeight: 700 }}>{firstName}</span>
                    </span>
                    <Avatar user={user} />
                  </button>
                ) : (
                  <button data-testid="topbar-signin"
                          onClick={() => nav("/app/signin?next=" + encodeURIComponent(pathname))}
                          style={{ padding: "6px 12px", borderRadius: 999,
                                   background: "transparent", color: "var(--ww-text)",
                                   border: "1px solid var(--ww-accent-ink)",
                                   fontFamily: "var(--f-head)", fontWeight: 700, fontSize: 12,
                                   textTransform: "lowercase", cursor: "pointer" }}>
                    get in
                  </button>
                )}
              </div>
              <NotificationsPanel open={panelOpen} onClose={() => setPanelOpen(false)}
                                     items={notif.items} refresh={notif.refresh}
                                     markOneReadNow={notif.markOneReadNow}
                                     markAllReadNow={notif.markAllReadNow}
                                     isDesktop={true} anchorRight={160} />
            </div>
          </header>
        ) : (
          <header data-testid="member-topbar" data-layout="phone"
                  className="relative z-20 flex flex-shrink-0 items-center justify-between px-4 glass glass-chrome"
                  style={{ height: 56, color: "var(--ww-text)",
                           borderLeft: 0, borderRight: 0, borderTop: 0 }}>
            <div data-testid="phone-greeting" style={{ minWidth: 68, flexShrink: 1,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user ? (
                <span style={{ fontFamily: "var(--f-head)", fontWeight: 500,
                                fontSize: 14, color: "var(--ww-text-muted)",
                                textTransform: "lowercase" }}>
                  hello,&nbsp;<span style={{ color: "var(--ww-text)", fontWeight: 700 }}>{firstName}</span>
                </span>
              ) : (
                <button data-testid="topbar-signin"
                        onClick={() => nav("/app/signin?next=" + encodeURIComponent(pathname))}
                        style={{ padding: "6px 10px", borderRadius: 999,
                                 background: "transparent", color: "var(--ww-text)",
                                 border: "1px solid var(--ww-accent-ink)",
                                 fontFamily: "var(--f-head)", fontWeight: 700, fontSize: 12,
                                 textTransform: "lowercase", cursor: "pointer", minHeight: 32 }}>
                  get in
                </button>
              )}
            </div>
            <LockupLogo width={116} ground={logoGround} />
            <div className="flex items-center gap-1">
              {!forcedNight && <ThemeToggle size={20} />}
              {user && (
                <HeartButton unread={notif.unread} onClick={openPanel}
                               label={`notifications${notif.unread ? `, ${notif.unread} new` : ""}`}
                               testId="topbar-notifications-phone" />
              )}
            </div>
            <NotificationsPanel open={panelOpen} onClose={() => setPanelOpen(false)}
                                   items={notif.items} refresh={notif.refresh}
                                   markOneReadNow={notif.markOneReadNow}
                                   markAllReadNow={notif.markAllReadNow}
                                   isDesktop={false} />
          </header>
        )
      )}

      <main className="relative z-10 flex-1 min-h-0" style={{ overflow: "hidden" }}>
        <Outlet />
      </main>

      <InsiderUnlocksSheet />
      <GuestIntro />
      <SignupCelebration />


      {!isDesktop && (
        <nav data-testid="member-bottomnav"
             className="relative z-20 flex-shrink-0 glass glass-chrome"
             style={{ borderLeft: 0, borderRight: 0, borderBottom: 0,
                      paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          <ul className="mx-auto flex max-w-2xl items-end justify-between px-4 py-1.5">
            {TABS.map(({ to, label, icon: Icon, testId, raised, end, badge }) => (
              <li key={to} className="flex-1">
                <NavLink to={to} end={end} data-testid={testId}
                         className={({ isActive }) => `flex flex-col items-center gap-0.5 py-1.5 text-[10px] uppercase tracking-[0.14em] ${isActive ? "font-bold" : ""}`}
                         style={({ isActive }) => ({
                           color: isActive ? "var(--ww-text)" : "var(--ww-text-muted)",
                           fontFamily: "var(--f-head)",
                         })}>
                  {raised ? (
                    <span className="grid h-12 w-12 -translate-y-3 place-items-center rounded-full"
                          style={{ background: "var(--kili)", color: "#1A1A30",
                                   boxShadow: "0 6px 20px rgba(255,214,10,0.35)" }}>
                      <Icon size={22} strokeWidth={2.2} />
                    </span>
                  ) : (
                    <span style={{ position: "relative", display: "inline-flex" }}>
                      <Icon size={20} strokeWidth={1.6} />
                      {badge === "yap" && yapUnread > 0 && (
                        <span data-testid="nav-yap-dot"
                              style={{ position: "absolute", top: -2, right: -4,
                                       width: 9, height: 9, borderRadius: 999,
                                       background: "#FF6B9D",
                                       boxShadow: "0 0 0 2px var(--ww-ground)" }} />
                      )}
                    </span>
                  )}
                  {!raised && <span>{label.toLowerCase()}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

function HeartButton({ unread, onClick, label, testId }) {
  return (
    <button aria-label={label} data-testid={testId} onClick={onClick}
            style={{ background: "transparent", border: 0, cursor: "pointer",
                     color: "var(--ww-text)", padding: 6, minHeight: 44, minWidth: 44,
                     display: "inline-flex", alignItems: "center", justifyContent: "center",
                     position: "relative" }}>
      <Heart size={20} strokeWidth={1.7} />
      {unread > 0 && (
        <span data-testid="topbar-heart-dot"
              className="ww-pulse-dot"
              style={{ position: "absolute", top: 8, right: 8,
                       width: 9, height: 9, borderRadius: 999,
                       background: "#FF6B9D",
                       boxShadow: "0 0 0 2px var(--ww-ground)" }} />
      )}
    </button>
  );
}

function Avatar({ user }) {
  const pic = user?.photo_url || user?.picture;
  const initial = ((user?.display_name || user?.handle || "?") + "").slice(0, 1).toUpperCase();
  if (pic) {
    return <img src={pic} alt="" style={{ width: 28, height: 28, borderRadius: 999, objectFit: "cover" }} />;
  }
  return (
    <span style={{ width: 28, height: 28, borderRadius: 999, background: "#FFD60A",
                   color: "#1A1A30", display: "grid", placeItems: "center",
                   fontFamily: "Poppins", fontWeight: 800, fontSize: 13 }}>
      {initial}
    </span>
  );
}



/**
 * Wave C · "you're in" celebration after a brand-new sign-up lands.
 * Reads the once-per-session flag from sessionStorage (set by AuthCallback /
 * JoinIntake / anywhere else that completes a sign-up) and dispatches a
 * small toast + yellow bar at the top. Zero DOM when flag is unset.
 */
function SignupCelebration() {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    if (consumeSignupFlag()) {
      setShow(true);
      const t = setTimeout(() => setShow(false), 3400);
      return () => clearTimeout(t);
    }
    const onEvt = () => {
      setShow(true);
      setTimeout(() => setShow(false), 3400);
    };
    window.addEventListener(SIGNUP_CELEBRATION_EVENT, onEvt);
    return () => window.removeEventListener(SIGNUP_CELEBRATION_EVENT, onEvt);
  }, []);
  if (!show) return null;
  return (
    <>
      <Confetti run={show} durationMs={1200} />
      <div data-testid="signup-celebration" role="status" aria-live="polite"
           style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 65,
                    display: "flex", justifyContent: "center", padding: "12px 16px",
                    pointerEvents: "none" }}>
        <div style={{ padding: "10px 18px", borderRadius: 999,
                       background: "#FFD60A", color: "#1A1A30",
                       fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                       textTransform: "lowercase",
                       boxShadow: "0 8px 24px rgba(10,10,20,0.18)" }}>
          {WELCOME_AFTER_SIGNUP}
        </div>
      </div>
    </>
  );
}
